import React, { useEffect, useState } from "react";
import LabelWithInput from "@components/input/LabelWithInput";
import DropdownInput from "@components/input/DropdownInput";
import {
  ColorDropdown,
  ConditionDrawer,
} from "@pages/register/_components/index";
import styled from "@emotion/styled";
import FadeInContainer from "@components/motion/FadeInContainer";
import { useFormContext } from "react-hook-form";
import SizeDropdown from "@pages/register/_components/SizeDropdown";
import useSelectedColorStore from "@store/register/useSelectedColor";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const ColorBoxWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  maxWidth: "80%",
  gap: "1rem",
}));

const ColorBox = styled.div<{ selected: boolean }>(({ theme, selected }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  fontSize: "1.2rem",
  lineHeight: "1.2rem",
  fontWeight: theme.fontWeight.medium,
  padding: "1rem",
  border: selected
    ? `1px solid ${theme.color.mint}`
    : `1px solid ${theme.color.black}`,

  backgroundColor: selected ? theme.color.light_mint : "",
}));

const ColorBall = styled.div<{ color: string }>(({ color }) => ({
  width: "1.2rem",
  height: "1.2rem",
  borderRadius: "50%",
  backgroundColor: color,
}));

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
}));

const ColorWithSize = ({ showSizeError }: { showSizeError?: boolean }) => {
  const { t } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState(false);
  const [openSizeDropdown, setOpenSizeDropdown] = useState(false);
  const [selectedColor, setSelectedColor] = useState(t("선택"));
  const theme = useTheme();
  const { watch, setValue } = useFormContext();
  const { color, setColor, setColorId } = useSelectedColorStore();
  const { getSizeList } = useApiRequest();
  const condition = watch("product_condition");

  const { data: sizeList } = useQuery({
    queryKey: ["sizeList", color],
    queryFn: () => getSizeList(watch("size_type_id")),
  });

  // 초기 렌더링시 첫번째 컬러 선택
  useEffect(() => {
    const stockList = watch("stock_list"); //  현재 선택한 색상의 사이즈 리스트

    if (stockList?.length > 0) {
      const firstColor = stockList[0]; // 첫번째 컬러 정보

      // 현재 선택된 color가 없을 때만 초기화
      if (!color) {
        setColor(firstColor.color_name); // 컬러 이름 설정
        setColorId(firstColor.color_id); // 컬러 ID 설정
      }
    }

    if (sizeList) {
      const stockListIndex = stockList.findIndex(
        (item: any) => item.color_name === color,
      );

      if (
        stockListIndex !== -1 &&
        stockList[stockListIndex]?.size_list.length === 0
      ) {
        // 해당 color의 size_list를 빈 배열로 초기화
        setValue(`stock_list.${stockListIndex}.size_list`, []);
      }
    }
  }, [sizeList]);

  const handleClickColorBox = (colorValue: string) => {
    const selectedColor = watch("stock_list").find(
      (item: any) => item.color_name === colorValue,
    );

    setColor(selectedColor.color_name);
    setColorId(selectedColor.color_id);
    setOpenSizeDropdown(false);
  };

  const handleDelete = (e: React.MouseEvent, colorName: string) => {
    e.stopPropagation();
    const currentProductInfo = watch("stock_list") || [];

    const updatedProductInfo = currentProductInfo.filter(
      (productInfo: any) => productInfo.color_name !== colorName,
    );

    if (updatedProductInfo.length > 0) {
      setSelectedColor(
        updatedProductInfo[updatedProductInfo.length - 1].color_name,
      );
      setColor(updatedProductInfo[updatedProductInfo.length - 1].color_name);
    } else {
      setSelectedColor(t("선택"));
      setColor("");
    }
    setValue("stock_list", updatedProductInfo);
  };

  const stock_list = watch("stock_list");

  // 에러 상태 직접 계산
  const hasColorError =
    !stock_list?.length || stock_list.some((stock: any) => !stock.color_id);

  const hasSizeError = stock_list?.some((stock: any) =>
    stock.size_list.some((size: any) => !size.size_id || size.amount < 1),
  );

  const { pathname } = useLocation();
  const isProductEdit = pathname === "/product-edit";

  return (
    <Container>
      <ColorBoxWrapper>
        {watch("stock_list")?.length > 0 &&
          watch("stock_list").map((item: any, index: number) => (
            <ColorBox
              key={index}
              selected={color === item.color_name}
              onClick={() => handleClickColorBox(item.color_name)}>
              <ColorBall color={item.hex_code} />

              {item.color_name}

              <button
                onClick={(e) => handleDelete(e, item.color_name)}
                type="button">
                <Image src={theme.image.close.default} size="12px" />
              </button>
            </ColorBox>
          ))}
      </ColorBoxWrapper>
      <FadeInContainer isShow={condition !== ""} nonAnimation={isProductEdit}>
        <LabelWithInput label="색상" required>
          {/* 색상 에러 */}

          {hasColorError && (
            <ErrorMessage>{t("색상을 선택해주세요.")}</ErrorMessage>
          )}

          <ColorDropdown
            name="stock_list"
            open={openDropdown}
            setOpen={setOpenDropdown}
            selectedValue={selectedColor}
            setSelectedValue={setSelectedColor}
          />
        </LabelWithInput>
      </FadeInContainer>
      <FadeInContainer
        isShow={watch(`stock_list`)?.length !== 0}
        nonAnimation={isProductEdit}>
        <LabelWithInput label="사이즈" required>
          {/* 사이즈 에러 */}
          {showSizeError && (
            <ErrorMessage>{t("사이즈와 수량을 입력해주세요.")}</ErrorMessage>
          )}

          <SizeDropdown
            options={sizeList}
            name={`stock_list[${watch("stock_list").findIndex((item: any) => item.color_name === color)}].size_list`}
            open={openSizeDropdown}
            setOpen={setOpenSizeDropdown}
          />
        </LabelWithInput>
      </FadeInContainer>
    </Container>
  );
};

export default ColorWithSize;
